import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import studentService from '../services/StudentService';
import departmentService from '../services/DepartmentService';

const StudentRegister = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    dob: '',
    gender: 'Male',
    address: '',
    admissionYear: new Date().getFullYear().toString(),
    admissionDate: new Date().toISOString().split('T')[0],
    aadhaarNumber: '',
    parentName: '',
    parentPhone: '',
    departmentName: '',
    courseName: '',
    className: '',
    institutionType: 'DEGREE_COLLEGE', // Fallback default
    photo: '',
    
    // Remaining details
    rollNo: '',
    admissionNo: '',
    bloodGroup: '',
    nationality: 'Indian',
    religion: '',
    category: '',
    city: '',
    state: '',
    pincode: '',
    fatherName: '',
    fatherPhone: '',
    motherName: '',
    motherPhone: '',
    guardianName: '',
    guardianPhone: '',
    guardianRelation: '',
    guardianEmail: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    medicalConditions: '',
    allergies: '',
    feeCategory: '',
    scholarshipStatus: 'NO',
    scholarshipId: '',
    hostelRequired: false,
    transportRequired: false,
    previousSchoolCollege: '',
    previousBoardUniversity: '',
    previousPassingYear: '',
    previousPercentageCgpa: ''
  });

  const [institutionName, setInstitutionName] = useState('');
  const [orgType, setOrgType] = useState(''); // Holds SCHOOL, PUC_COLLEGE, DEGREE_COLLEGE
  const [departments, setDepartments] = useState([]); // Holds departments for this organization
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Responsive layout state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    
    // Fetch organization details and departments on mount
    const fetchOrgAndDeptDetails = async () => {
      try {
        const response = await studentService.getOrganizationDetails(orgId);
        if (response && response.success) {
          setInstitutionName(response.institutionName || response.orgName || '');
          if (response.institutionType) {
            setOrgType(response.institutionType);
            setFormData((prev) => ({
              ...prev,
              institutionType: response.institutionType,
            }));
          }
        }
      } catch (err) {
        console.error('Error fetching organization details:', err);
      }

      try {
        const deptResponse = await departmentService.getActiveDepartmentsByOrgIdStr(orgId);
        if (Array.isArray(deptResponse)) {
          setDepartments(deptResponse);
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };

    fetchOrgAndDeptDetails();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [orgId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Photo size should be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          photo: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateStep = (step) => {
    const phoneRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    switch (step) {
      case 1:
        if (!formData.institutionType) {
          return 'Institution Type is required.';
        }
        if (formData.institutionType === 'SCHOOL' && !formData.className.trim()) {
          return 'Class Name is required for School.';
        }
        return null;

      case 2:
        if (!formData.firstName.trim()) {
          return 'First Name is required.';
        }
        if (!formData.lastName.trim()) {
          return 'Last Name is required.';
        }
        if (!formData.phone.trim()) {
          return 'Phone Number is required.';
        }
        if (!phoneRegex.test(formData.phone.trim().replace(/\s+/g, ''))) {
          return 'Student Phone Number must be a valid 10-digit number.';
        }
        if (!formData.email.trim()) {
          return 'Email address is required.';
        }
        if (!emailRegex.test(formData.email.trim())) {
          return 'Please enter a valid email address.';
        }
        if (!formData.dob) {
          return 'Date of Birth is required.';
        }
        return null;

      case 3:
        if (formData.pincode.trim() && !/^\d{6}$/.test(formData.pincode.trim())) {
          return 'Pincode must be a 6-digit number.';
        }
        if (formData.fatherPhone.trim() && !phoneRegex.test(formData.fatherPhone.trim().replace(/\s+/g, ''))) {
          return "Father's Phone Number must be a 10-digit number.";
        }
        if (formData.motherPhone.trim() && !phoneRegex.test(formData.motherPhone.trim().replace(/\s+/g, ''))) {
          return "Mother's Phone Number must be a 10-digit number.";
        }
        if (formData.guardianPhone.trim() && !phoneRegex.test(formData.guardianPhone.trim().replace(/\s+/g, ''))) {
          return "Guardian's Phone Number must be a 10-digit number.";
        }
        return null;

      case 4:
        if (formData.emergencyContactPhone.trim() && !phoneRegex.test(formData.emergencyContactPhone.trim().replace(/\s+/g, ''))) {
          return "Emergency Contact Phone Number must be a 10-digit number.";
        }
        return null;

      default:
        return null;
    }
  };

  const handleNext = () => {
    setError('');
    const stepError = validateStep(currentStep);
    if (stepError) {
      setError(stepError);
      return;
    }
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setError('');
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate last step and all overall steps
    for (let i = 1; i <= totalSteps; i++) {
      const stepError = validateStep(i);
      if (stepError) {
        setCurrentStep(i);
        setError(stepError);
        return;
      }
    }

    // Combine fields for request
    const studentName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
    const payload = {
      ...formData,
      name: studentName,
      parentName: formData.parentName || formData.fatherName || formData.motherName || formData.guardianName || '',
      parentPhone: formData.parentPhone || formData.fatherPhone || formData.motherPhone || formData.guardianPhone || '',
    };

    try {
      setLoading(true);
      const response = await studentService.preRegisterStudentByQr(orgId, payload);
      if (response && response.success) {
        setSuccess(true);
      } else {
        setError(response.message || 'Failed to submit registration. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'An error occurred during registration. Please check details and try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', transition: 'border-color 0.2s' };
  const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '11px', fontWeight: '600', color: '#4b5563', textTransform: 'uppercase' };
  const sectionTitleStyle = { fontSize: '14px', color: '#2a5298', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px', margin: '0 0 16px 0', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' };

  if (success) {
    return (
      <div className="login-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px', overflowY: 'auto' }}>
        <div className="login-card" style={{ maxWidth: '500px', width: '100%', padding: '40px', textAlign: 'center', background: 'white', borderRadius: '16px', flexDirection: 'column' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1e3a8a', marginBottom: '16px' }}>Registration Submitted!</h2>
          <p style={{ fontSize: '15px', color: '#4b5563', lineHeight: '1.6', marginBottom: '24px' }}>
            Thank you, <strong>{formData.firstName} {formData.lastName}</strong>. Your pre-registration details have been successfully submitted to <strong>{institutionName || orgId}</strong>.
          </p>
          <div style={{ background: '#f3f4f6', padding: '16px', borderRadius: '8px', marginBottom: '24px', textAlign: 'left', fontSize: '13px', color: '#374151' }}>
            <strong>Note:</strong> Your account will remain inactive until the administration completes the original document verification. A temporary password has been allocated for future use.
          </div>
          <button
            onClick={() => navigate('/login')}
            className="btn btn-primary"
            style={{ width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}
          >
            Go to Login Page
          </button>
        </div>
      </div>
    );
  }

  // Helper step text
  const stepLabels = ['Academic', 'Personal', 'Family & Address', 'Facilities & Past Info'];

  return (
    <div className="login-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '100vh', padding: isMobile ? '10px' : '40px 20px', overflowY: 'auto', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
      <div className="login-card" style={{ maxWidth: '1000px', width: '100%', margin: 'auto', flexDirection: isMobile ? 'column' : 'row', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', overflow: 'hidden', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.8)' }}>
        
        {/* Left Side: Branding info */}
        <div className="login-branding" style={{ padding: isMobile ? '30px 20px' : '40px', flex: isMobile ? 'none' : '1.1', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: isMobile ? 'auto' : '550px' }}>
          <div className="branding-logo" style={{ width: '80px', height: '80px', fontSize: '36px', marginBottom: '16px' }}>📲</div>
          <h2 className="branding-title" style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: '12px' }}>Pre-Registration</h2>
          <p className="branding-subtitle" style={{ fontSize: '14px', marginBottom: '20px', maxWidth: '300px' }}>
            Scan the QR code to fill out details. Once verified, your account will be activated.
          </p>
          <ul className="branding-features" style={{ textAlign: 'left', fontSize: '13px' }}>
            <li style={{ justifyContent: 'flex-start' }}>Fast online enrollment</li>
            <li style={{ justifyContent: 'flex-start' }}>Document verification queue</li>
            <li style={{ justifyContent: 'flex-start' }}>Safe & secure submission</li>
          </ul>
        </div>

        {/* Right Side: Form details */}
        <div className="login-form-section" style={{ padding: isMobile ? '24px 16px' : '36px', flex: '1.4', background: 'white' }}>
          <h2 className="login-title" style={{ textAlign: 'left', fontSize: '24px', fontWeight: '700', marginBottom: '4px', width: '100%' }}>
            Student Details
          </h2>
          <p style={{ marginTop: 0, marginBottom: '20px', fontSize: '14px', color: '#4b5563' }}>
            Institution: <strong style={{ color: '#2a5298' }}>{institutionName || orgId}</strong>
          </p>

          {/* Elegant Modern Stepper Progress Bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', padding: '0 10px', position: 'relative' }}>
            {/* Background line connecting steps */}
            <div style={{ position: 'absolute', top: '15px', left: '10%', right: '10%', height: '2px', background: '#e2e8f0', zIndex: 0 }}></div>
            {/* Active colored line */}
            <div style={{ position: 'absolute', top: '15px', left: '10%', width: `${((currentStep - 1) / (totalSteps - 1)) * 80}%`, height: '2px', background: '#2a5298', zIndex: 0, transition: 'width 0.3s ease' }}></div>
            
            {stepLabels.map((label, idx) => {
              const stepNum = idx + 1;
              const isActive = stepNum === currentStep;
              const isCompleted = stepNum < currentStep;
              return (
                <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, zIndex: 1, cursor: stepNum <= currentStep ? 'pointer' : 'default' }} onClick={() => stepNum <= currentStep && setCurrentStep(stepNum)}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: isCompleted ? '#2a5298' : isActive ? 'white' : '#f1f5f9',
                    border: isActive ? '3px solid #2a5298' : isCompleted ? '2px solid #2a5298' : '2px solid #cbd5e1',
                    color: isCompleted ? 'white' : isActive ? '#2a5298' : '#64748b',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: '700',
                    fontSize: '13px',
                    transition: 'all 0.3s ease'
                  }}>
                    {isCompleted ? '✓' : stepNum}
                  </div>
                  <span style={{ fontSize: '10px', marginTop: '6px', fontWeight: isActive ? '700' : '500', color: isActive ? '#2a5298' : '#64748b', textAlign: 'center', width: '70px', whiteSpace: 'normal', lineHeight: '1.2' }}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            {error && (
              <div className="alert alert-danger" style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '8px', background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c', fontSize: '14px' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px 20px', marginBottom: '24px', minHeight: '260px' }}>
              
              {/* STEP 1: INSTITUTION & STREAM */}
              {currentStep === 1 && (
                <>
                  <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                    <h4 style={sectionTitleStyle}>Step 1: Institution & Academic Details</h4>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Institution Type *</label>
                    <select
                      name="institutionType"
                      className="form-control"
                      value={formData.institutionType}
                      onChange={handleChange}
                      style={{ ...inputStyle, background: 'white' }}
                    >
                      {orgType === 'SCHOOL' ? (
                        <option value="SCHOOL">School</option>
                      ) : orgType === 'PUC_COLLEGE' || orgType === 'DEGREE_COLLEGE' ? (
                        <>
                          <option value="DEGREE_COLLEGE">Degree College</option>
                          <option value="PUC_COLLEGE">PUC College</option>
                        </>
                      ) : (
                        <>
                          <option value="DEGREE_COLLEGE">Degree College</option>
                          <option value="PUC_COLLEGE">PUC College</option>
                          <option value="SCHOOL">School</option>
                        </>
                      )}
                    </select>
                  </div>

                  {formData.institutionType === 'SCHOOL' ? (
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={labelStyle}>Class Name *</label>
                      <input
                        type="text"
                        name="className"
                        className="form-control"
                        placeholder="e.g. 10th Standard, UKG"
                        value={formData.className}
                        onChange={handleChange}
                        required
                        style={inputStyle}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={labelStyle}>Department Name</label>
                        {departments.length > 0 ? (
                          <select
                            name="departmentName"
                            className="form-control"
                            value={formData.departmentName}
                            onChange={handleChange}
                            style={{ ...inputStyle, background: 'white' }}
                          >
                            <option value="">Select Department</option>
                            {departments.map((dept) => (
                              <option key={dept.departmentId} value={dept.departmentName}>
                                {dept.departmentName}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            name="departmentName"
                            className="form-control"
                            placeholder="e.g. Computer Science, Commerce"
                            value={formData.departmentName}
                            onChange={handleChange}
                            style={inputStyle}
                          />
                        )}
                      </div>
                      <div className="form-group" style={{ margin: 0, gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                        <label style={labelStyle}>Course / Degree Name</label>
                        <input
                          type="text"
                          name="courseName"
                          className="form-control"
                          placeholder="e.g. Bachelor of Science, Bachelor of Commerce"
                          value={formData.courseName}
                          onChange={handleChange}
                          style={inputStyle}
                        />
                      </div>
                    </>
                  )}

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Roll Number</label>
                    <input
                      type="text"
                      name="rollNo"
                      className="form-control"
                      placeholder="Enter roll number"
                      value={formData.rollNo}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Admission Number</label>
                    <input
                      type="text"
                      name="admissionNo"
                      className="form-control"
                      placeholder="Enter admission number"
                      value={formData.admissionNo}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>
                </>
              )}

              {/* STEP 2: PERSONAL DETAILS */}
              {currentStep === 2 && (
                <>
                  <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                    <h4 style={sectionTitleStyle}>Step 2: Personal Details</h4>
                  </div>

                  <div className="form-group" style={{ gridColumn: isMobile ? 'span 1' : 'span 2', display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                    <label style={{ ...labelStyle, textAlign: 'center', marginBottom: '10px' }}>Student Profile Photo</label>
                    <div style={{ position: 'relative', width: '100px', height: '100px', borderRadius: '50%', overflow: 'hidden', background: '#e2e8f0', border: '3px solid #2a5298', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                      {formData.photo ? (
                        <img src={formData.photo} alt="Student Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: '40px' }}>👤</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <label style={{ padding: '8px 16px', background: '#2a5298', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'inline-block', transition: 'background 0.2s' }}>
                        Upload Photo
                        <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
                      </label>
                      {formData.photo && (
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, photo: '' }))} style={{ padding: '8px 16px', background: '#ef4444', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600', border: 'none', cursor: 'pointer', transition: 'background 0.2s' }}>
                          Remove
                        </button>
                      )}
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '8px' }}>JPEG or PNG, Max size 2MB</span>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      className="form-control"
                      placeholder="Enter first name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      className="form-control"
                      placeholder="Enter last name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      className="form-control"
                      placeholder="Enter phone number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Date of Birth *</label>
                    <input
                      type="date"
                      name="dob"
                      className="form-control"
                      value={formData.dob}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Gender</label>
                    <select
                      name="gender"
                      className="form-control"
                      value={formData.gender}
                      onChange={handleChange}
                      style={{ ...inputStyle, background: 'white' }}
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Aadhaar Number</label>
                    <input
                      type="text"
                      name="aadhaarNumber"
                      className="form-control"
                      placeholder="12-digit Aadhaar number"
                      value={formData.aadhaarNumber}
                      onChange={handleChange}
                      maxLength="12"
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Blood Group</label>
                    <select
                      name="bloodGroup"
                      className="form-control"
                      value={formData.bloodGroup}
                      onChange={handleChange}
                      style={{ ...inputStyle, background: 'white' }}
                    >
                      <option value="">Select Blood Group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Nationality</label>
                    <input
                      type="text"
                      name="nationality"
                      className="form-control"
                      placeholder="e.g. Indian"
                      value={formData.nationality}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Religion</label>
                    <input
                      type="text"
                      name="religion"
                      className="form-control"
                      placeholder="e.g. Hindu, Christian, Muslim"
                      value={formData.religion}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Category</label>
                    <input
                      type="text"
                      name="category"
                      className="form-control"
                      placeholder="e.g. General, OBC, SC, ST"
                      value={formData.category}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>
                </>
              )}

              {/* STEP 3: ADDRESS & PARENTS */}
              {currentStep === 3 && (
                <>
                  <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                    <h4 style={sectionTitleStyle}>Step 3: Address & Parents Details</h4>
                  </div>

                  <div className="form-group" style={{ margin: 0, gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                    <label style={labelStyle}>Residential Address</label>
                    <textarea
                      name="address"
                      className="form-control"
                      placeholder="Enter complete residential address"
                      value={formData.address}
                      onChange={handleChange}
                      rows="2"
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>City</label>
                    <input
                      type="text"
                      name="city"
                      className="form-control"
                      placeholder="Enter city"
                      value={formData.city}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>State</label>
                    <input
                      type="text"
                      name="state"
                      className="form-control"
                      placeholder="Enter state"
                      value={formData.state}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Pincode</label>
                    <input
                      type="text"
                      name="pincode"
                      className="form-control"
                      placeholder="6-digit pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      maxLength="6"
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2', height: '10px' }}></div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Father Name</label>
                    <input
                      type="text"
                      name="fatherName"
                      className="form-control"
                      placeholder="Enter father's name"
                      value={formData.fatherName}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Father Phone</label>
                    <input
                      type="tel"
                      name="fatherPhone"
                      className="form-control"
                      placeholder="Enter father's phone"
                      value={formData.fatherPhone}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Mother Name</label>
                    <input
                      type="text"
                      name="motherName"
                      className="form-control"
                      placeholder="Enter mother's name"
                      value={formData.motherName}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Mother Phone</label>
                    <input
                      type="tel"
                      name="motherPhone"
                      className="form-control"
                      placeholder="Enter mother's phone"
                      value={formData.motherPhone}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Guardian Name</label>
                    <input
                      type="text"
                      name="guardianName"
                      className="form-control"
                      placeholder="Enter guardian name"
                      value={formData.guardianName}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Guardian Phone</label>
                    <input
                      type="tel"
                      name="guardianPhone"
                      className="form-control"
                      placeholder="Enter guardian phone"
                      value={formData.guardianPhone}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Guardian Relation</label>
                    <input
                      type="text"
                      name="guardianRelation"
                      className="form-control"
                      placeholder="e.g. Uncle, Aunt"
                      value={formData.guardianRelation}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Guardian Email</label>
                    <input
                      type="email"
                      name="guardianEmail"
                      className="form-control"
                      placeholder="Enter guardian email"
                      value={formData.guardianEmail}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>
                </>
              )}

              {/* STEP 4: EMERGENCY, ACADEMICS & FACILITIES */}
              {currentStep === 4 && (
                <>
                  <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                    <h4 style={sectionTitleStyle}>Step 4: Emergency, Facilities & Past Academics</h4>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Emergency Contact Name</label>
                    <input
                      type="text"
                      name="emergencyContactName"
                      className="form-control"
                      placeholder="Contact person name"
                      value={formData.emergencyContactName}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Emergency Contact Phone</label>
                    <input
                      type="tel"
                      name="emergencyContactPhone"
                      className="form-control"
                      placeholder="Emergency phone number"
                      value={formData.emergencyContactPhone}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Medical Conditions</label>
                    <input
                      type="text"
                      name="medicalConditions"
                      className="form-control"
                      placeholder="e.g. Asthma, Diabetes (if any)"
                      value={formData.medicalConditions}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Allergies</label>
                    <input
                      type="text"
                      name="allergies"
                      className="form-control"
                      placeholder="e.g. Penicillin, Peanuts (if any)"
                      value={formData.allergies}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2', height: '10px' }}></div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Previous School/College</label>
                    <input
                      type="text"
                      name="previousSchoolCollege"
                      className="form-control"
                      placeholder="Institution last attended"
                      value={formData.previousSchoolCollege}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Board / University</label>
                    <input
                      type="text"
                      name="previousBoardUniversity"
                      className="form-control"
                      placeholder="e.g. CBSE, State Board"
                      value={formData.previousBoardUniversity}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Percentage / CGPA</label>
                    <input
                      type="text"
                      name="previousPercentageCgpa"
                      className="form-control"
                      placeholder="e.g. 85%"
                      value={formData.previousPercentageCgpa}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Passing Year</label>
                    <input
                      type="text"
                      name="previousPassingYear"
                      className="form-control"
                      placeholder="e.g. 2025"
                      value={formData.previousPassingYear}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ gridColumn: isMobile ? 'span 1' : 'span 2', height: '10px' }}></div>

                  <div className="form-group" style={{ margin: 0, display: 'flex', gap: '20px', alignItems: 'center', height: '100%', gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                    <label style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        name="hostelRequired"
                        checked={formData.hostelRequired}
                        onChange={handleChange}
                        style={{ width: '18px', height: '18px' }}
                      />
                      Hostel Required
                    </label>
                    <label style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        name="transportRequired"
                        checked={formData.transportRequired}
                        onChange={handleChange}
                        style={{ width: '18px', height: '18px' }}
                      />
                      Transport Required
                    </label>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Fee Category</label>
                    <input
                      type="text"
                      name="feeCategory"
                      className="form-control"
                      placeholder="e.g. Regular, Merit"
                      value={formData.feeCategory}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={labelStyle}>Scholarship Status</label>
                    <select
                      name="scholarshipStatus"
                      className="form-control"
                      value={formData.scholarshipStatus}
                      onChange={handleChange}
                      style={{ ...inputStyle, background: 'white' }}
                    >
                      <option value="NO">No</option>
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ margin: 0, gridColumn: isMobile ? 'span 1' : 'span 2' }}>
                    <label style={labelStyle}>Scholarship ID</label>
                    <input
                      type="text"
                      name="scholarshipId"
                      className="form-control"
                      placeholder="Enter scholarship ID if any"
                      value={formData.scholarshipId}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>
                </>
              )}

            </div>

            {/* Form Nav Buttons */}
            <div style={{ display: 'flex', gap: '16px', marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="btn btn-secondary"
                  style={{ flex: 1, padding: '12px', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}
                >
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="btn btn-secondary"
                  style={{ flex: 1, padding: '12px', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}
                >
                  Cancel
                </button>
              )}
              
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="btn btn-primary"
                  style={{ flex: 2, padding: '12px', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ flex: 2, padding: '12px', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}
                >
                  {loading ? 'Submitting...' : 'Register Student'}
                </button>
              )}
            </div>
          </form>
        </div>

      </div>
    </div>
  );
};

export default StudentRegister;
